import { ReactElement, useState } from 'react';
import { Box, Theme, Typography } from '@mui/material';

import { HealthState, ProductFeature } from '@localstack/types';

import { SERVICE_CODES } from '@localstack/constants';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { getLowestTierForFeature } from '@localstack/services';

import classnames, { Argument } from 'classnames';

import { PlanChip } from '../../feedback';

import { AwsServiceIcon, AwsServiceIconProps } from '../AwsServiceIcon';
import { SystemStatus as SystemStatusIcon } from '../SystemStatus';
import { FeatureUnavailableDialog } from '../../feedback/FeatureUnavailableDialog';

type Props = AwsServiceIconProps & {
  status?: HealthState;
  clickable?: boolean;
  urlOpenerFunc: () => void;
  navigateTo: () => void;
  className?: Argument;
  hasFeature: (feature: ProductFeature, variant?: string) => boolean;
  /** When true, the upsell dialog skips Freemium/Hobby as the suggested upgrade tier. */
  isFreemiumHobbyCommercialMisuse?: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    treatAsButton: {
      alignItems: 'center',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      cursor: 'pointer',
      width: '100%',
      padding: theme.spacing(0, 1),
    },
    fullWidth: {
      width: '100%',
    },

    serviceUnavailable: {
      filter: 'grayscale(1)',
      opacity: '0.7',
    },
  }),
);

export const AwsServiceTile = ({
  code,
  title,
  size,
  hideTooltip,
  status,
  clickable,
  urlOpenerFunc,
  navigateTo,
  className,
  hasFeature,
  isFreemiumHobbyCommercialMisuse,
}: Props): ReactElement => {
  /*
  Assume a service is pro if not found in static list.
  This way each time a new service is added and the Console is not up to date
  it will still show as pro in the status page.
  */
  const classes = useStyles();
  const [openFeatureUnavailableDialog, setOpenFeatureUnavailableDialog] = useState<boolean>(false);

  const isServiceAvailable = hasFeature(ProductFeature.FEATURE_LOCALSTACK_AWS_PROVIDER, `/^${code}:.+$/`);

  const handleResourceClick = () => {
    if (!isServiceAvailable) {
      return setOpenFeatureUnavailableDialog(true);
    }

    navigateTo();
  };

  const availablePlan = getLowestTierForFeature(ProductFeature.FEATURE_LOCALSTACK_AWS_PROVIDER, `/^${code}:.+$/`, {
    skipFreemium: isFreemiumHobbyCommercialMisuse,
  });

  return (
    <div role="listitem" className={classes.fullWidth}>
      <FeatureUnavailableDialog
        open={openFeatureUnavailableDialog}
        onClose={() => setOpenFeatureUnavailableDialog(false)}
        urlOpenerFunc={urlOpenerFunc}
        availablePlan={availablePlan}
      />
      <Box
        display="flex"
        width="100%"
        className={classnames(
          {
            [classes.serviceUnavailable]: !isServiceAvailable,
          },
          className,
          classes.treatAsButton,
        )}
        role="button"
        id={code}
        onClick={clickable ? handleResourceClick : undefined}
      >
        <Box alignItems="center" display="flex" width="100%">
          <AwsServiceIcon code={code} title={title} size={size} hideTooltip={hideTooltip} />
          <Box>
            <Typography style={{ whiteSpace: 'normal' }} variant="h6">
              {SERVICE_CODES[code as keyof typeof SERVICE_CODES] || code}
            </Typography>

            {status && (
              <Typography variant="body2">
                <SystemStatusIcon state={status} />
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          {!isServiceAvailable && <PlanChip planFamily={availablePlan} showTooltip />}
        </Box>
      </Box>
    </div>
  );
};
